// Legend.jsx – Dynamic legend aligned with real API
import React, { useState, useEffect } from "react";
import "./Legend.css";
import ArrowIcon from "../assets/icons/arrow-icon.svg";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckIcon from "@mui/icons-material/Check";
import { fetchLegend } from "../api/legendsApi";

const DEFAULT_LEGEND = {
  title: "راهنما",
  stops: [
    { min: -1, max: -0.8, color: "#a50026" },
    { min: -0.8, max: -0.6, color: "#d73027" },
    { min: -0.6, max: -0.4, color: "#f46d43" },
    { min: -0.4, max: -0.2, color: "#fdae61" },
    { min: -0.2, max: 0, color: "#fee090" },
    { min: 0, max: 0.2, color: "#ffffbf" },
    { min: 0.2, max: 0.4, color: "#e0f3f8" },
    { min: 0.4, max: 0.6, color: "#abd9e9" },
    { min: 0.6, max: 0.8, color: "#74add1" },
    { min: 0.8, max: 1, color: "#4575b4" },
  ],
};

const Legend = ({
  isOpen,
  toggleLegend,
  hasBottomChart,
  activeProductType, // product type string, e.g., "NDVI"
  legendData: legendDataFromProduct, // pre‑fetched legend (optional)
  productConfig, // { minValue, maxValue } (optional)
  steps = 10, // number of stops (optional)
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [classification, setClassification] = useState("auto");
  const [legendData, setLegendData] = useState(DEFAULT_LEGEND);
  const [loading, setLoading] = useState(false);

  const positionClass = hasBottomChart
    ? "with-bottom-chart"
    : "no-bottom-chart";

  // Persian digit conversion — handles negative sign correctly
  // and wraps the result in a LTR mark so bidi rendering doesn't flip the minus
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "—";
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
    const isNeg = num < 0;
    const abs = Math.abs(num)
      .toFixed(2)
      .replace(/\d/g, (d) => persianDigits[parseInt(d)]);
    // \u200F = Right-to-Left Mark, keeps the number inside RTL flow correctly
    return isNeg ? `\u200F-${abs}` : abs;
  };

  // ------------------------------------------------------------------
  // Fetch legend from API when activeProductType changes
  // ------------------------------------------------------------------
  useEffect(() => {
    // If parent supplied pre‑fetched legend data, use it directly
    if (legendDataFromProduct?.stops?.length) {
      const sorted = [...legendDataFromProduct.stops].sort(
        (a, b) => a.min - b.min,
      );
      setLegendData({
        // Prefer the Persian display name from productConfig over API title
        title: productConfig?.displayName || legendDataFromProduct.title || activeProductType || "راهنما",
        stops: sorted,
        vmin: legendDataFromProduct.vmin,
        vmax: legendDataFromProduct.vmax,
      });
      return;
    }

    if (!activeProductType) {
      setLegendData(DEFAULT_LEGEND);
      return;
    }

    const fetchLegendData = async () => {
      setLoading(true);
      try {
        const data = await fetchLegend(activeProductType);

        const sorted =
          data.stops?.sort((a, b) => a.min - b.min) || DEFAULT_LEGEND.stops;

        setLegendData({
          // Always prefer the Persian display name from productConfig
          title: productConfig?.displayName || data.title || activeProductType,
          stops: sorted,
          vmin: data.vmin,
          vmax: data.vmax,
        });
      } catch (err) {
        console.warn("Legend fetch failed, using fallback:", err);
        setLegendData({
          ...DEFAULT_LEGEND,
          title: productConfig?.displayName || activeProductType || "راهنما",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLegendData();
  }, [activeProductType, legendDataFromProduct, productConfig, steps]);

  const handleClassificationChange = (type) => {
    setClassification(type);
    setIsSettingsOpen(false);
    if (type === "palette") console.log("Open Palette Modal");
    else if (type === "manual") console.log("Open Manual Modal");
  };

  return (
    <div
      className={`legend-container ${positionClass} ${isOpen ? "open" : "closed"}`}
    >
      <div className="legend-header">
        <button className="legend-toggle" onClick={toggleLegend}>
          <img src={ArrowIcon} alt="Toggle" className="arrow-icon" />
          <span className="legend-title-text">راهنما</span>
        </button>

        {/* Settings icon – only when expanded */}
        {isOpen && (
          <div className="classification-settings">
            <SettingsIcon
              className="settings-icon"
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            />

            {isSettingsOpen && (
              <div className="settings-dropdown">
                <div
                  className={`dropdown-item ${classification === "auto" ? "active" : ""}`}
                  onClick={() => handleClassificationChange("auto")}
                >
                  {classification === "auto" && (
                    <CheckIcon fontSize="inherit" />
                  )}
                  <span>طبقه‌بندی خودکار</span>
                </div>
                <div
                  className={`dropdown-item ${classification === "palette" ? "active" : ""}`}
                  onClick={() => handleClassificationChange("palette")}
                >
                  {classification === "palette" && (
                    <CheckIcon fontSize="inherit" />
                  )}
                  <span>طبقه‌بندی پالت پیش‌فرض</span>
                </div>
                <div
                  className={`dropdown-item ${classification === "manual" ? "active" : ""}`}
                  onClick={() => handleClassificationChange("manual")}
                >
                  {classification === "manual" && (
                    <CheckIcon fontSize="inherit" />
                  )}
                  <span>طبقه‌بندی دستی</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div id="legend-content" className="legend-content">
        {loading ? (
          <div className="legend-loading">در حال بارگذاری...</div>
        ) : (
          <>
            <div className="legend-subtitle">{legendData.title}</div>
            {legendData.stops.map((stop, index) => (
              <div key={index} className="legend-row">
                <div
                  className="color-box"
                  style={{ background: stop.color }}
                />
                {/* bdi isolates each number from the surrounding RTL context
                    so the minus sign stays on the left of its digit */}
                <span className="range-text" dir="rtl">
                  <bdi>{formatNumber(stop.min)}</bdi>
                  {" تا "}
                  <bdi>{formatNumber(stop.max)}</bdi>
                </span>
              </div>
            ))}
            {legendData.vmin !== undefined && legendData.vmax !== undefined && (
              <div className="legend-range-info">
                <span>
                  محدوده: {formatNumber(legendData.vmin)} تا{" "}
                  {formatNumber(legendData.vmax)}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Legend;
